import { defineStore } from 'pinia'
import { Task } from '@/stores/task'
import { API } from '@/script/api'
import { watch } from 'vue'
import { WSChannel } from '@/script/ws_channel'
import { ENV } from '@/script/proj_env'
import { VOCImageProvider, VOCImageRequestInfo, VOCImageRef } from '@/script/voc_img_tool'
import { downImage, downVideo } from '@/utils/downLoadFile'
import { AnalyticHub } from '@/script/analytic'
import { User } from '@/script/user'

const channel = new WSChannel(ENV.currentConfig.swapAPIHost, '/ai/prompt_channel')
export const useTxtToImgTaskManager = defineStore('txtToImgTaskManager', {
    state: () => {
        return {
            tasks: [],
            watchChannelEventStopHandler: null,
        };
    },
    getters: {
        /// 是否有生成中的任务(包含提交、通道)
        hasGenerateProcessingTask: (state) => {
            let processing = false
            for (let task of state.tasks) {
                if (task.inGenerateProcessing) {
                    processing = true
                    break
                }
            }
            return processing
        },
        /// 获取所有在通道中生成的数据
        allInChannelProcessingTasks: (state) => {
            let tasks = []
            for (let task of state.tasks) {
                if (task.inChannelProcessing) {
                    tasks.push(task)
                }
            }
            return tasks
        },
    },
    actions: {
        /// 提交任务，原始参数提交
        async submit({
            prompt,
            styleKey,
            ratio,
            faceId,
            batch,
        }) {
            const submitParams = {
                prompt: prompt,
                styleKey: styleKey,
                ratio: ratio,
                faceId: faceId,
                batch: batch,
            }
            const tasks = []
            for (let i = 0; i < batch; i++) {
                tasks.push(new Task(null, submitParams))
            }
            this.tasks.unshift(...tasks)
            await this.submitSameBatchTasks(tasks)
            return tasks
        },
        /// 提交任务, 基于同一批次tasks提交
        async submitSameBatchTasks(tasks) {
            const task = tasks[0]
            const submitParams = JSON.parse(JSON.stringify(task.submitParams))
            submitParams.batch = tasks.length
            const resp = await API.txtToImgSubmit(submitParams)
            if (resp.code == 200) {
                if (tasks.length == 1) {
                    tasks[0].leftTime = resp.data.leftTime
                    tasks[0].paintingTime = resp.data.paintingTime
                    tasks[0].taskId = resp.data.taskId
                    tasks[0].taskStatus = resp.data.taskStatus
                }else {
                    for (let i = 0; i < tasks.length; i++) {
                        tasks[i].leftTime = resp.data[i].leftTime
                        tasks[i].paintingTime = resp.data[i].paintingTime
                        tasks[i].taskId = resp.data[i].taskId
                        tasks[i].taskStatus = resp.data[i].taskStatus
                    }
                }
                this.channelBindTask(tasks)
            }else {
                for (const task of tasks) {
                    task.taskStatus = Task.STATUS_GENERATE_ERROR
                }
            }
        },
        /// 重试任务
        async retry(retryTask) {
            for (const task of this.tasks) {
                if (task === retryTask) {
                    if (task.taskStatus == Task.STATUS_GENERATE_ERROR) {
                        await this.submitSameBatchTasks(task.submitParams)
                    }else if (task.taskStatus == Task.STATUS_DONWLOAD_ERROR) {
                        this.downloadTaskImage(task)
                    }
                    break
                }
            }
        },
        /// 绑定任务和通道，确保通道能正常推送数据到当前通道
        async channelBindTask(tasks) {
            if (!channel.active) {
                let success = await channel.open()
                if (!success) {
                    return false
                }
                let event = channel.listener()
                this.watchChannelEventStopHandler = watch(event.statusCode, () => {
                    switch (event.statusCode.value.code) {
                        case -1: { // 后端任务生成错误
                            let task = this.getTaskByIds(event.data.taskId, event.data.localTaskId)
                            if ((typeof event.data) == 'object') {
                                task?.updateStatus(Task.STATUS_GENERATE_ERROR, event.data.data.message || null)
                            }else {
                                task?.updateStatus(Task.STATUS_GENERATE_ERROR, event.data || null)
                            }
                        }break
                        case WSChannel.ConnectionErrorCode: { // 通道错误
                            let processingTasks = this.allInChannelProcessingTasks
                            for (let task of processingTasks) {
                                task.updateStatus(Task.STATUS_GENERATE_ERROR)
                            }
                            this.closeChannelIfNeed()
                        }break
                        case 2: { // 生成中
                            console.log('🐳 - 2 ', event.data.data.taskId)
                            let task = this.getTaskByIds(event.data.data.taskId, event.data.data.localTaskId)
                            task?.updateStatus(Task.STATUS_PAINTING)
                        }break
                        case 3: { // 完成
                            console.log('🐳 - 3 ', event.data.data.taskId)
                            let task = this.getTaskByIds(event.data.data.taskId, event.data.data.localTaskId)
                            if (task) {
                                if (event.data.data.user) {
                                    User.getInstance.syncUserInfo(event.data.data.user)
                                }else {
                                    User.getInstance.loadUserInfo()
                                }
                                task.fileData = event.data.data.file
                                this.downloadTaskImage(task)
                            }
                            this.closeChannelIfNeed()
                        }break
                        default:
                            break;
                    }
                })
            }
            for (const _ of tasks) {
                channel.sendVerifyMessage()
            }
            return true
        },
        /// 符合条件则关闭ws通道
        closeChannelIfNeed() {
            if (this.hasGenerateProcessingTask) return
                channel.dispose()
            if (this.watchChannelEventStopHandler) {
                this.watchChannelEventStopHandler()
            }
        },
        /// 根据taskId、localTaskId获取任务
        getTaskByIds(taskId, localTaskId) {
            for (let task of this.tasks) {
                if (task.taskId == taskId && task.localTaskId == localTaskId) {
                    return task
                }
            }
            return null
        },
        /// 下载任务中图片媒体数据
        async downloadTaskImage(task) {
            task.updateStatus(Task.STATUS_DOWNLOADING)
            let url = task.fileData.fileUrl
            if (!url) {
                task.updateStatus(Task.STATUS_DONWLOAD_ERROR)
                return
            }
            let request = new VOCImageRequestInfo({
                url: url
            })
            let imgRef = VOCImageProvider.getInstance.loadImage({
                request: request,
                higherPriority: true,
            })
            let downloadStatusWatchStopHandler
            downloadStatusWatchStopHandler = watch(imgRef.imageStatus, () => {
                if (imgRef.imageStatus.value == VOCImageRef.IMAGE_STATUS_DONE) {
                    downloadStatusWatchStopHandler()
                    task.imageBlobUrl = imgRef.image.url
                    task.updateStatus(Task.STATUS_COMPLETE)
                }else if (imgRef.imageStatus.value == VOCImageRef.IMAGE_STATUS_FAILED) {
                    task.updateStatus(Task.STATUS_DONWLOAD_ERROR)
                    downloadStatusWatchStopHandler()
                }
            })
        },
    },
})